import { useRef } from "react";
import type { KeyboardEvent, ReactElement } from "react";

import type { IconName } from "../../design-system/icons";
import { Icon } from "../atoms/Icon";
import styles from "./Tabs.module.css";

export interface TabItem {
  readonly id: string;
  readonly label: string;
  /** A count shown after the label (the mockup's `.tab .num`), e.g. the size of a gallery tab. */
  readonly count?: number | undefined;
  /** A glyph before the label (the mockup's preview tabs); the label stays the name. */
  readonly icon?: IconName | undefined;
  /** A tooltip for the tab, when its label alone does not say what it holds. */
  readonly title?: string | undefined;
}

export interface TabsProps {
  /** The accessible name of the tablist (from `t()`). */
  readonly label: string;
  /** Prefix of the tab and panel ids; one per tablist on the page. */
  readonly idBase: string;
  readonly items: readonly TabItem[];
  readonly selected: string;
  readonly onSelect: (id: string) => void;
}

const tabId = (idBase: string, id: string): string => `${idBase}-tab-${id}`;
const panelId = (idBase: string, id: string): string => `${idBase}-panel-${id}`;

/** The attributes of the panel a tab controls; spread them on the element that shows its content. */
export function tabPanelProps(
  idBase: string,
  id: string,
): { id: string; role: "tabpanel"; "aria-labelledby": string; tabIndex: 0 } {
  return { id: panelId(idBase, id), role: "tabpanel", "aria-labelledby": tabId(idBase, id), tabIndex: 0 };
}

/**
 * The WAI-ARIA tabs pattern with automatic activation (the mockup's `.tabs`).
 *
 * One tab stop: only the selected tab is in the tab order (roving `tabIndex`), so Tab goes from the
 * tablist straight to its panel. Left and Right move AND select, wrapping at both ends; Home and End
 * jump to the ends. Selecting is the caller's state, so a tab can also mirror the URL.
 */
export function Tabs({ label, idBase, items, selected, onSelect }: TabsProps): ReactElement {
  const refs = useRef(new Map<string, HTMLButtonElement>());
  // The one tab stop: the selected tab, or the first one when the selection matches none (m4). Only
  // the selected tab names a panel, because only its panel is rendered.
  const stop = items.some((item) => item.id === selected) ? selected : items[0]?.id;

  const move = (event: KeyboardEvent<HTMLDivElement>): void => {
    const current = items.findIndex((item) => item.id === selected);
    const last = items.length - 1;
    const targets: Partial<Record<string, number>> = {
      ArrowRight: current === last ? 0 : current + 1,
      ArrowLeft: current <= 0 ? last : current - 1,
      Home: 0,
      End: last,
    };
    const index = targets[event.key];
    const target = index === undefined ? undefined : items[index];
    if (target === undefined) return;
    event.preventDefault();
    onSelect(target.id);
    refs.current.get(target.id)?.focus();
  };

  return (
    <div className={styles.tabs} role="tablist" aria-label={label} onKeyDown={move} tabIndex={-1}>
      {items.map((item) => {
        const isSelected = item.id === selected;
        return (
          <button
            key={item.id}
            ref={(element) => {
              if (element === null) refs.current.delete(item.id);
              else refs.current.set(item.id, element);
            }}
            type="button"
            role="tab"
            title={item.title}
            id={tabId(idBase, item.id)}
            className={styles.tab}
            aria-selected={isSelected}
            aria-controls={isSelected ? panelId(idBase, item.id) : undefined}
            tabIndex={item.id === stop ? 0 : -1}
            onClick={() => {
              if (!isSelected) onSelect(item.id);
            }}
          >
            {item.icon === undefined ? null : <Icon name={item.icon} size="sm" />}
            <span data-tab-label="">{item.label}</span>
            {item.count === undefined ? null : (
              <>
                {" "}
                <span className={styles.count} data-tab-count="">{item.count}</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
